import React from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebookF, faInstagram, IconDefinition } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope } from '@fortawesome/free-regular-svg-icons';

import { FOOTER_LINKS, SOCIAL_LINKS } from '@config/footer';

import boonLogo from '@assets/images/boon-logo-footer.png';
import styles from './footer.styles';
import Markup from '../markup/markup';

const icons = {
    facebook: faFacebookF,
    instagram: faInstagram,
    email: faEnvelope,
};

export interface FooterProps {
    menuLinks?: FooterMenuItem[];
    socialLinks?: { [key: string]: FooterSocialItem };
    icons?: { [key: string]: IconDefinition };
    logoLink?: string;
    isLogoAbsolute?: boolean;
}

interface FooterMenuItem {
    text: string;
    link: string;
    absolute?: boolean;
}

interface FooterSocialItem {
    link: string;
    icon: string;
}

const Footer: React.FC<FooterProps> = props => {
    const { t } = useTranslation('footer');

    const generateLogo = (href = '#') => (
        <a href={href} className="footerLogoLink mb-4 lg:mb-0 lg:pl-4 xl:pl-0">
            <img src={boonLogo} className="h-11 object-contain" />
        </a>
    );

    const generateLink = item => {
        if (item.absolute) {
            return (
                <a
                    href={item.link}
                    className="my-1 lg:my-0 base-text-size-7 text-center lg:text-left w-full inline-flex items-center justify-center lg:justify-start whitespace-no-wrap"
                >
                    {t(item.text)}
                </a>
            );
        }

        return (
            <Link href={item.link}>
                <a className="my-1 lg:my-0 base-text-size-7 text-center lg:text-left w-full inline-flex items-center justify-center lg:justify-start whitespace-no-wrap">
                    {t(item.text)}
                </a>
            </Link>
        );
    };

    return (
        <footer className="w-screen flex justify-center flex-col items-center bg-boon-black py-3 xl:py-4">
            <style jsx>{styles}</style>

            <div className="base-content-width-wide xl:base-content-width-wide-1 flex flex-col flex-no-wrap lg:flex-row items-center justify-between text-white font-light">
                {props.isLogoAbsolute ? (
                    generateLogo(props.logoLink)
                ) : (
                    <Link href={props.logoLink}>{generateLogo()}</Link>
                )}

                <div className="hidden lg:block Separator mx-10 xl:mx-12 border-l border-solid h-16 border-ashblue"></div>
                <div className="lg:hidden Separator border-b border-solid w-12 mt-2 mb-6 border-ashblue mx-12"></div>

                <Markup
                    className="h-full base-text-size-8 text-center whitespace-no-wrap mb-6 lg:mb-0 lg:mr-10 leading-tight"
                    content={t('footer-copyright')}
                />

                <div className="lg:flex flex-wrap justify-center h-full mb-6 lg:mb-0 lg:flex-no-wrap">
                    {Object.keys(props.socialLinks).map(key => (
                        <a
                            key={key}
                            className={`
                                border border-solid border-white rounded-full inline-flex items-center justify-center
                                h-6 w-6 mx-2 my-1 text-xs
                                lg:h-7 lg:w-7 lg:text-md
                                xl:h-8 xl:w-8 xl:text-base
                            `}
                            href={props.socialLinks[key].link}
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            <FontAwesomeIcon icon={props.icons[props.socialLinks[key].icon]} />
                        </a>
                    ))}
                </div>

                <div className="hidden lg:block Separator mx-10 xl:mx-12 border-l border-solid h-16 border-ashblue"></div>

                <div className="h-full mb-4 lg:mb-0">
                    <div className="h-full uppercase text-xs flex flex-col lg:grid lg:grid-flow-col lg:row-gap-1 lg:col-gap-8 lg:grid-rows-2">
                        {Object.keys(props.menuLinks).map(key => (
                            <div key={key}>{generateLink(props.menuLinks[key])}</div>
                        ))}
                    </div>
                </div>
            </div>
        </footer>
    );
};

Footer.defaultProps = {
    logoLink: '/',
    isLogoAbsolute: false,
    menuLinks: FOOTER_LINKS,
    socialLinks: SOCIAL_LINKS,
    icons,
};

export default Footer;
